import yaml from "js-yaml";
import { marked } from "marked";

const experienceModules = import.meta.glob("../../_posts/experience/*.markdown", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export interface ExperienceCompany {
  id: string;
  title: string;
  description: string;
  year?: string;
  client: string;
  logo?: string;
  about: string;
  projects: string;
  responsibilities: string[];
  review?: string;
  reviewName?: string;
  video?: string;
  /** Fallback images from experience frontmatter when no portfolio projects match. */
  mediaImages: string[];
  contentHtml: string;
  legacySlugs: string[];
}

/** Maps legacy Jekyll permalink segments to canonical experience ids */
export const EXPERIENCE_SLUG_ALIASES: Record<string, string> = {
  "7ofd": "7ofd",
  intobridge: "7ofd",
  baxi: "baxi",
  yotta: "yotta",
  begrip: "begrip",
  buttonsystems: "buttonsystems",
  toonect: "buttonsystems",
  sos: "sos",
  polygraficka: "sos",
};

function slugFromPath(path: string): string {
  const filename = path.split("/").pop() ?? "";
  return filename.replace(/\.markdown$/, "");
}

function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith("---")) return { data: {}, content: raw };
  const end = raw.indexOf("\n---", 3);
  if (end === -1) return { data: {}, content: raw };
  const frontmatter = raw.slice(3, end);
  const content = raw.slice(end + 4).replace(/^\r?\n/, "");
  const data = yaml.load(frontmatter) as Record<string, unknown> | null;
  return { data: data ?? {}, content };
}

function parseString(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === "string").join(" ");
  }
  return "";
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function resolveId(categories: string[], filename: string): string {
  const cat = categories[0]?.toLowerCase();
  if (cat && EXPERIENCE_SLUG_ALIASES[cat]) return EXPERIENCE_SLUG_ALIASES[cat];
  if (cat) return cat;
  return slugFromPath(filename);
}

function parseExperienceMediaImages(data: Record<string, unknown>): string[] {
  const images: string[] = [];
  if (typeof data.img === "string" && data.img) images.push(data.img);
  const img2 = data["img-2"] ?? data.img2;
  if (typeof img2 === "string" && img2) images.push(img2);
  for (let i = 3; i <= 6; i++) {
    const hyphen = data[`img-${i}`];
    const plain = data[`img${i}`];
    if (typeof hyphen === "string" && hyphen) images.push(hyphen);
    else if (typeof plain === "string" && plain) images.push(plain);
  }
  return images;
}

function legacySlugsFromPermalink(permalink: unknown, id: string): string[] {
  const slugs = new Set<string>([id]);
  if (typeof permalink === "string") {
    const segment = permalink.replace(/^ux-design-resume\/?/i, "").replace(/\/$/, "");
    if (segment) {
      slugs.add(segment.toLowerCase());
      const mapped = EXPERIENCE_SLUG_ALIASES[segment.toLowerCase()];
      if (mapped) slugs.add(mapped);
    }
  }
  return [...slugs];
}

function parseCompanies(): ExperienceCompany[] {
  return Object.entries(experienceModules)
    .map(([path, raw]) => {
      const { data, content } = parseFrontmatter(raw);
      const categories = (() => {
        const raw = data.categories;
        if (typeof raw === "string") return [raw];
        if (Array.isArray(raw)) return raw.filter((c): c is string => typeof c === "string");
        return [];
      })();
      const id = resolveId(categories, path);
      const title = typeof data.title === "string" ? data.title : id;

      return {
        id,
        title,
        description: parseString(data.description),
        year: typeof data.year === "string" ? data.year : undefined,
        client: typeof data.client === "string" ? data.client : id,
        logo: typeof data.logo === "string" ? data.logo : undefined,
        about: parseString(data.about),
        projects: parseString(data.projects),
        responsibilities: parseStringArray(data.responsibilities),
        review: parseString(data.review) || undefined,
        reviewName: parseString(data["review-name"]) || undefined,
        video: typeof data.video === "string" ? data.video : undefined,
        mediaImages: parseExperienceMediaImages(data),
        contentHtml: (marked.parse(content) as string) || "",
        legacySlugs: legacySlugsFromPermalink(data.permalink, id),
      } satisfies ExperienceCompany;
    })
    .sort((a, b) => (b.year ?? "").localeCompare(a.year ?? ""));
}

let cached: ExperienceCompany[] | null = null;

export function getExperienceCompanies(): ExperienceCompany[] {
  if (!cached) cached = parseCompanies();
  return cached;
}

export function getExperienceCompany(id: string): ExperienceCompany | undefined {
  const normalized = id.toLowerCase();
  const alias = EXPERIENCE_SLUG_ALIASES[normalized] ?? normalized;
  return getExperienceCompanies().find(
    (c) => c.id === alias || c.legacySlugs.includes(normalized),
  );
}

export function resolveExperienceId(slug: string): string | undefined {
  const normalized = slug.toLowerCase();
  return EXPERIENCE_SLUG_ALIASES[normalized] ?? getExperienceCompany(normalized)?.id;
}
